import SnackbarAlert from '~/components/SnackbarAlert/SnackbarAlert';
import { useSnackbarAlert } from '~/components/SnackbarAlert/hooks';
import { AddSnackbarAlertType, AddSnackbarActionAlertType } from '~/components/SnackbarAlert/types';
import { FC, createContext, useContext, useEffect } from 'react';
import { Trans } from '~/utils/translate';
import { useSelector } from 'react-redux';
import { getSnackbarStore } from '~/selectors/snackbar.selectors';

type GlobalComponentsContext = {
  snackbar: {
    addSnackbarAlert: AddSnackbarAlertType;
    addSnackbarActionAlert: AddSnackbarActionAlertType;
  };
};

const GlobalComponentsContext = createContext<GlobalComponentsContext>({
  snackbar: {
    addSnackbarAlert: () => {},
    addSnackbarActionAlert: () => {},
  },
});

/**
 * A global provider that makes globally available components that can be shared across multiple routes
 * by exposing their hooks and mutation functions
 * @param children
 * @returns a GlobalComponentProvider with the global components and {children} in it.
 */
export const GlobalComponentsProvider: FC = ({ children }) => {
  const { snackbarAlert, addSnackbarAlert, addSnackbarActionAlert } = useSnackbarAlert();

  const snackbarStore = useSelector(getSnackbarStore);
  useEffect(() => {
    if (!snackbarStore.message) return;

    addSnackbarAlert(
      snackbarStore.i18n ? <Trans i18nKey={snackbarStore.message} /> : snackbarStore.message,
      snackbarStore.severity,
    );
  }, [addSnackbarAlert, snackbarStore]);

  const globalComponents = {
    snackbar: {
      addSnackbarAlert,
      addSnackbarActionAlert,
    },
  };

  return (
    <GlobalComponentsContext.Provider value={globalComponents}>
      {snackbarAlert ? <SnackbarAlert {...snackbarAlert} /> : null}
      {children}
    </GlobalComponentsContext.Provider>
  );
};

export const useGlobalComponentsContext = () => {
  const { snackbar } = useContext(GlobalComponentsContext);

  return { snackbar };
};
